import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Alert,
  AlertTitle,
  Typography,
  Chip,
  Paper,
  Collapse,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAuth } from "@healthdesk/shared-hooks";
import { TIER_DISPLAY_NAMES } from "@healthdesk/shared-utils";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import PriceCard from "./PriceCard";
import { getFirebaseHttpsCallableUserMessage } from "../../../utils/firebaseHttpsCallableUserMessage";

const FEATURE_COMPARISON = [
  { label: "Ask Me Anything", free: "Basic (5/day)", small: "Basic (30/mo)", medium: "Advanced (200/mo)", large: "Advanced (Unlimited*)" },
  { label: "Symptom Checker", free: "1 session/mo", small: "3 sessions/mo", medium: "7 sessions/mo", large: "Unlimited*" },
  { label: "Voice Conversation", free: "No", small: "No", medium: "Yes", large: "Yes" },
  { label: "CMR in Symptom Check", free: "No", small: "No", medium: "Yes", large: "Yes" },
  { label: "Diagnostic Test Suggestions", free: "No", small: "No", medium: "Yes", large: "Yes" },
  { label: "Upload & Discuss Results", free: "No", small: "No", medium: "No", large: "Yes" },
  { label: "CMR (Medical Records)", free: "Yes", small: "Yes", medium: "Yes", large: "Yes" },
  { label: "CMR Share", free: "Yes", small: "Yes", medium: "Yes", large: "Yes" },
  { label: "Medication Tracking", free: "No", small: "No", medium: "Yes", large: "Yes" },
  { label: "Data Links", free: "No", small: "No", medium: "Yes", large: "Yes" },
];

const PricingControls = ({
  prices,
  selectedInterval,
  onIntervalChange,
  sendToCheckout,
  loading,
  canStartTrial,
  startTrial,
  trialLoading,
  subscription,
  role,
  trialExpired,
  experimentPrices,
  experimentArm,
}) => {
  const {
    hasActiveSubscription,
    hasLapsedSubscription,
    subscriptionData,
    userData,
    isTierFree,
  } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(null); // null or priceId
  const [portalLoading, setPortalLoading] = useState(null); // null, priceId, or 'manage'


  // Get the user's current subscription interval to set as default
  const getCurrentSubscriptionInterval = () => {
    const interval = subscriptionData?.items?.[0]?.price?.recurring?.interval;
    if (!interval) return "monthly";

    // Map Stripe intervals to our UI intervals
    if (interval === "year") return "yearly";
    if (interval === "month") return "monthly";
    if (interval === "day") return "daily";
    return "monthly"; // fallback
  };

  // Use current subscription interval as default if user has active subscription
  const _defaultInterval = hasActiveSubscription
    ? getCurrentSubscriptionInterval()
    : "monthly";

  // Set the interval to match user's current subscription on mount (only once)
  useEffect(() => {
    if (
      hasActiveSubscription &&
      subscriptionData?.items?.[0]?.price?.recurring?.interval
    ) {
      const currentInterval = getCurrentSubscriptionInterval();
      // Only set if we haven't set it yet (avoid fighting with user clicks)
      if (selectedInterval === "monthly" && currentInterval === "yearly") {
        onIntervalChange(currentInterval);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveSubscription, subscriptionData]);

  // Check if we have new tiered pricing structure
  const hasTimedPricing = () => {
    return Object.keys(prices).some(
      (key) =>
        typeof prices[key] === "object" &&
        prices[key] !== null &&
        !prices[key].id
    );
  };

  // Check if this is facility pricing structure
  const hasFacilityPricing = () => {
    return prices.facility && typeof prices.facility === "object";
  };

  // Legacy/simple: single plan with month + year (and optional day) keys
  const hasLegacyPricing = () => {
    return (
      (prices.month || prices.year) &&
      !hasFacilityPricing() &&
      !hasTimedPricing()
    );
  };

  // Check if this is the user's current plan
  const isCurrentPlan = (priceId) => {
    if (!subscriptionData?.items?.[0]?.price?.id) return false;
    return subscriptionData.items[0].price.id === priceId;
  };

  // NEW: Unified checkout function that handles both new subscriptions and upgrades
  const handleCheckout = async (priceId, isDaily = false) => {
    setCheckoutLoading(priceId);
    try {
      const createCheckoutSession = firebase
        .functions()
        .httpsCallable("createCheckoutSession");

      const { data } = await createCheckoutSession({
        priceId,
        successUrl: window.location.origin + "/dashboard",
        cancelUrl: window.location.href,
        mode: isDaily ? "payment" : "subscription",
        // Explicit intent: either use existing customer or create new
        ...(userData?.stripeId
          ? { stripeId: userData.stripeId }
          : { createNew: true }),
        metadata: {
          ...(isDaily && {
            type: "daily_pass",
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          }),
        },
      });

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      const { userMessage } = getFirebaseHttpsCallableUserMessage(error);
      alert(userMessage);
      setCheckoutLoading(null);
    }
  };

  // Handle manage subscription click - goes to portal for plan changes
  const handleManageClick = async (targetPriceId) => {
    setPortalLoading(targetPriceId);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.origin + "/dashboard/settings", // Go to settings on successful plan change
        cancelUrl: window.location.href, // Stay on current page if canceled
        role: userData?.role,
        stripeId: userData?.stripeId,
        flowType: "subscription_update_confirm",
        flowData: {
          subscription_update_confirm: {
            subscription: subscriptionData?.id, // The subscription ID, not the item
            items: [
              {
                id: subscriptionData?.items?.[0]?.id, // Current subscription item ID
                price: targetPriceId, // New price ID they want to switch to
              },
            ],
          },
        },
      });

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error creating portal session:", error);
      const { userMessage } = getFirebaseHttpsCallableUserMessage(error);
      alert(userMessage);
      setPortalLoading(null); // Only set loading to false on error
    }
  };

  // Handle regular portal access (for billing/cancellation)
  const handleRegularPortal = async () => {
    setPortalLoading("manage");
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.origin + "/dashboard/settings", // Go to settings on successful actions
        cancelUrl: window.location.href, // Stay on current page if canceled
        role: userData?.role,
        stripeId: userData?.stripeId,
      });

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error creating portal session:", error);
      const { userMessage } = getFirebaseHttpsCallableUserMessage(error);
      alert(userMessage);
      setPortalLoading(null); // Only set loading to false on error
    }
  };

  if (Object.keys(prices).length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Only show daily passes if no active subscription and not lapsed
  const showDaily = !hasActiveSubscription && !hasLapsedSubscription;

  const showIntervalToggle =
    (hasTimedPricing() && !hasFacilityPricing()) || hasLegacyPricing();

  const legacySavingsPercentage =
    hasLegacyPricing() && prices.month && prices.year
      ? Math.round(
          ((parseFloat(prices.month.price.replace("$", "")) * 12 -
            parseFloat(prices.year.price.replace("$", ""))) /
            (parseFloat(prices.month.price.replace("$", "")) * 12)) *
            100
        )
      : null;

  return (
    <>
      {showIntervalToggle && (
        <Box sx={{ textAlign: "center", pt: 4, mb: 4 }}>
          <ToggleButtonGroup
            value={selectedInterval}
            exclusive
            onChange={(e, newInterval) => {
              if (newInterval !== null) {
                onIntervalChange(newInterval);
              }
            }}
            sx={{
              mb: 4,
              borderRadius: "20px",
              backgroundColor: "grey.100",
              border: "1px solid",
              borderColor: "divider",
              position: "relative",
              "& .MuiToggleButton-root": {
                px: 2.5,
                py: 0.75,
                border: "none",
                borderRadius: 0,
                fontSize: "0.875rem",
                fontWeight: 500,
                minWidth: "80px",
                backgroundColor: "transparent",
                color: "text.secondary",
                transition: "all 0.2s ease-in-out",
                "&:not(:last-child)": {
                  borderRight: "1px solid",
                  borderRightColor: "divider",
                },
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -1,
                    bottom: -1,
                    left: 0,
                    right: 0,
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: "inherit",
                    pointerEvents: "none",
                    transition: "all 0.2s ease-in-out",
                  },
                },
                "&:hover:not(.Mui-selected)": {
                  backgroundColor: "grey.200",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -1,
                    bottom: -1,
                    left: 0,
                    right: 0,
                    border: "1px solid transparent",
                    borderRadius: "inherit",
                    pointerEvents: "none",
                  },
                },
                "&:first-of-type": {
                  borderTopLeftRadius: "20px",
                  borderBottomLeftRadius: "20px",
                  "&.Mui-selected::before, &:hover::before": {
                    borderRadius: "20px 0 0 20px",
                  },
                },
                "&:last-of-type": {
                  borderTopRightRadius: "20px",
                  borderBottomRightRadius: "20px",
                  "&.Mui-selected::before, &:hover::before": {
                    borderRadius: "0 20px 20px 0",
                  },
                },
              },
            }}
          >
            {showDaily && hasTimedPricing() && !hasFacilityPricing() && (
              <ToggleButton value="daily">Daily</ToggleButton>
            )}
            {showDaily && hasLegacyPricing() && prices.day && (
              <ToggleButton value="daily">Daily</ToggleButton>
            )}
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly" sx={{ position: "relative" }}>
              Yearly
              {(hasTimedPricing() || (hasLegacyPricing() && legacySavingsPercentage != null)) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -28,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    fontSize: "0.7rem",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    zIndex: 10,
                  }}
                >
                  {hasLegacyPricing() && legacySavingsPercentage != null
                    ? `Save ${legacySavingsPercentage}%`
                    : "Save 31%"}
                </Box>
              )}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={4}
        sx={{ mb: 4, alignItems: "stretch" }}
      >
        {hasFacilityPricing() ? (
          // Facility pricing: side-by-side monthly/yearly
          ["monthly", "yearly"].map((interval) => {
            const currentPrice = prices.facility?.[interval];
            if (!currentPrice) return null;

            // Calculate savings for yearly plan
            const isYearly = interval === "yearly";
            const monthlyPrice = prices.facility?.monthly;
            const yearlyPrice = prices.facility?.yearly;
            let savingsPercentage = null;

            if (isYearly && monthlyPrice && yearlyPrice) {
              const monthlyAnnual =
                parseInt(monthlyPrice.price.replace("$", "")) * 12;
              const yearlyAnnual = parseInt(yearlyPrice.price.replace("$", ""));
              const savings = monthlyAnnual - yearlyAnnual;
              savingsPercentage = Math.round((savings / monthlyAnnual) * 100);
            }

            return (
              <PriceCard
                key={interval}
                title={`${
                  interval.charAt(0).toUpperCase() + interval.slice(1)
                } Plan`}
                price={currentPrice}
                sendToCheckout={handleCheckout}
                loading={checkoutLoading}
                isDaily={false}
                canStartTrial={canStartTrial}
                startTrial={startTrial}
                trialLoading={trialLoading}
                isCurrentPlan={isCurrentPlan(currentPrice.id)}
                onManageClick={handleManageClick}
                onRegularPortal={handleRegularPortal}
                portalLoading={portalLoading}
                savingsPercentage={isYearly ? savingsPercentage : null}
              />
            );
          })
        ) : hasTimedPricing() ? (
          // Four-tier pricing: Free (when applicable) / Small / Medium / Large
          <>
            {role === "patient" && isTierFree && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  minWidth: 220,
                  flex: 1,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {TIER_DISPLAY_NAMES.free}
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                  $0
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Basic access — no payment required.
                </Typography>
                <Chip label="Your Current Plan" color="default" />
              </Paper>
            )}
            {["small", "medium", "large"].map((tier) => {
              const tierPrices = prices[tier];
              const currentPrice = tierPrices?.[selectedInterval];

              if (!currentPrice) return null;

              const isMostPopular = tier === "medium";
              const displayName = TIER_DISPLAY_NAMES[tier] || tier;

              return (
                <PriceCard
                  key={tier}
                  title={displayName}
                  price={currentPrice}
                  sendToCheckout={handleCheckout}
                  loading={checkoutLoading}
                  isDaily={selectedInterval === "daily"}
                  tier={tier}
                  canStartTrial={canStartTrial}
                  startTrial={startTrial}
                  trialLoading={trialLoading}
                  isCurrentPlan={isCurrentPlan(currentPrice.id)}
                  onManageClick={handleManageClick}
                  onRegularPortal={handleRegularPortal}
                  portalLoading={portalLoading}
                  highlight={isMostPopular}
                  badge={isMostPopular ? "Most Popular" : null}
                />
              );
            })}
          </>
        ) : (
          // Legacy pricing: one card for the selected interval (when toggle shown) or both cards
          (() => {
            const legacyMonth = prices.month;
            const legacyYear = prices.year;
            const legacyDay = prices.day;
            if (showIntervalToggle) {
              const intervalToKey = { monthly: "month", yearly: "year", daily: "day" };
              let key = intervalToKey[selectedInterval];
              let price = key === "month" ? legacyMonth : key === "year" ? legacyYear : legacyDay;
              if (!price) {
                key = legacyMonth ? "month" : legacyYear ? "year" : legacyDay ? "day" : null;
                price = key === "month" ? legacyMonth : key === "year" ? legacyYear : legacyDay;
              }
              if (!price) return null;
              const isYearly = key === "year";
              return (
                <PriceCard
                  key={key}
                  title={
                    key === "day"
                      ? "Day Pass"
                      : `${key === "month" ? "Monthly" : "Yearly"} Plan`
                  }
                  description={key === "day" ? "24-hour access. Does not auto-renew." : undefined}
                  price={price}
                  sendToCheckout={handleCheckout}
                  loading={checkoutLoading}
                  isDaily={key === "day"}
                  canStartTrial={canStartTrial}
                  startTrial={startTrial}
                  trialLoading={trialLoading}
                  isCurrentPlan={isCurrentPlan(price.id)}
                  onManageClick={handleManageClick}
                  onRegularPortal={handleRegularPortal}
                  portalLoading={portalLoading}
                  savingsPercentage={isYearly ? legacySavingsPercentage : null}
                />
              );
            }
            return (
              <>
                {legacyMonth && (
                  <PriceCard
                    title="Monthly Plan"
                    price={legacyMonth}
                    sendToCheckout={handleCheckout}
                    loading={checkoutLoading}
                    isDaily={false}
                    canStartTrial={canStartTrial}
                    startTrial={startTrial}
                    trialLoading={trialLoading}
                    isCurrentPlan={isCurrentPlan(legacyMonth.id)}
                    onManageClick={handleManageClick}
                    onRegularPortal={handleRegularPortal}
                    portalLoading={portalLoading}
                  />
                )}
                {legacyYear && (
                  <PriceCard
                    title="Yearly Plan"
                    price={legacyYear}
                    sendToCheckout={handleCheckout}
                    loading={checkoutLoading}
                    isDaily={false}
                    canStartTrial={canStartTrial}
                    startTrial={startTrial}
                    trialLoading={trialLoading}
                    isCurrentPlan={isCurrentPlan(legacyYear.id)}
                    onManageClick={handleManageClick}
                    onRegularPortal={handleRegularPortal}
                    portalLoading={portalLoading}
                    savingsPercentage={legacySavingsPercentage}
                  />
                )}
                {showDaily && legacyDay && (
                  <PriceCard
                    title="Day Pass"
                    description="24-hour access. Does not auto-renew."
                    price={legacyDay}
                    sendToCheckout={handleCheckout}
                    loading={checkoutLoading}
                    isDaily
                    canStartTrial={canStartTrial}
                    startTrial={startTrial}
                    trialLoading={trialLoading}
                    isCurrentPlan={isCurrentPlan(legacyDay.id)}
                    onManageClick={handleManageClick}
                    onRegularPortal={handleRegularPortal}
                    portalLoading={portalLoading}
                  />
                )}
              </>
            );
          })()
        )}
      </Stack>

      {(role === "patient" || role === "p2" || role === "p4") &&
        !trialExpired &&
        Object.keys(prices).length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Alert severity="info" sx={{ mt: 4, mb: 2 }}>
              <AlertTitle>Important Subscriber Information</AlertTitle>
              Thank you for considering a subscription with us! We want to
              ensure the best experience for all users, so please be aware that
              all subscriptions are subject to use restrictions. These
              guidelines are in place to help maintain the overall system's
              viability and ensure fair access for everyone. We appreciate your
              understanding and cooperation.
            </Alert>
          </Box>
        )}

      {role === "patient" && hasTimedPricing() && (
        <FeatureComparisonSection />
      )}
    </>
  );
};

function FeatureComparisonSection() {
  const [open, setOpen] = useState(false);
  const tiers = ["free", "small", "medium", "large"];
  const tierLabels = tiers.map((t) => TIER_DISPLAY_NAMES[t]);

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      <Button
        onClick={() => setOpen((o) => !o)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: "none", fontWeight: 600, mb: 1 }}
      >
        Compare Plans
      </Button>
      <Collapse in={open}>
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.85rem",
              "& th, & td": { px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", textAlign: "center" },
              "& th": { fontWeight: 600, backgroundColor: "grey.50" },
              "& td:first-of-type, & th:first-of-type": { textAlign: "left" },
            }}
          >
            <thead>
              <tr>
                <th>Feature</th>
                {tierLabels.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_COMPARISON.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{row.free}</td>
                  <td>{row.small}</td>
                  <td>{row.medium}</td>
                  <td>{row.large}</td>
                </tr>
              ))}
            </tbody>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5, pt: 0 }}>
            <strong>* MasterChart Enhanced:</strong> We reserve the right to limit usage if we
            determine, in our sole discretion, that unlimited use is being abused.
          </Typography>
        </Paper>
      </Collapse>
    </Box>
  );
}

export default PricingControls;
